import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Sparkles,
  X,
  Loader2,
  ChevronDown,
  ChevronRight,
  Check,
  AlertCircle,
  Info,
} from 'lucide-react';
import { aiRoomsForecastPreview, aiRoomsForecastCommit } from '../../api';
import type { AiRoomsForecastCommitResponse } from '../../api';
import type {
  AiRoomsForecastDay,
  AiRoomsForecastMonthSummary,
  AiRoomsForecastPreviewAvailable,
  AiRoomsReferenceSource,
} from '../../types';
import { formatCurrency } from '../../utils/formatters';

interface AiRoomsForecastModalProps {
  versionId: number;
  // The Revenue department the panel was opened on — the forecast is applied
  // as that department's Room Forecast driver
  departmentName?: string;
  departmentId?: number | null;
  currency?: string | null;
  onClose: () => void;
  onApplied: (result: AiRoomsForecastCommitResponse) => void;
}

type Phase = 'loading' | 'unavailable' | 'preview' | 'applying' | 'error';

// 'YYYY-MM' -> "July 2026" (constructed locally to avoid TZ shifts)
function formatMonthLabel(monthKey: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  if (!y || !m) return monthKey;
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

// 'YYYY-MM-DD' -> "Tue, Jul 7" (constructed locally to avoid TZ shifts)
function formatDayLabel(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return date;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

const SUPPORTED_PCT = 0.5;

const REFERENCE_LABELS: Record<AiRoomsReferenceSource, string> = {
  ly: 'last year',
  budget: 'budget',
  recent: 'recent weeks',
};

// A month is worth applying when most future days have learned pickup or a
// reference final to lean on (legacy previews carry only pickupSupportPct)
function isForecastable(m: AiRoomsForecastMonthSummary): boolean {
  return (m.forecastablePct ?? m.pickupSupportPct ?? 1) >= SUPPORTED_PCT;
}

function formatRefreshed(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null
    : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatRooms(value: number): string {
  return Math.round(value).toLocaleString();
}

export function AiRoomsForecastModal({ versionId, departmentName, departmentId = null, currency = 'USD', onClose, onApplied }: AiRoomsForecastModalProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [preview, setPreview] = useState<AiRoomsForecastPreviewAvailable | null>(null);
  const [unavailableReason, setUnavailableReason] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());
  // Months to apply. Defaults to months with future days the pickup curve has
  // history for — further out the "forecast" is just on-the-books, and those
  // days are better left on the system projection.
  const [selectedMonths, setSelectedMonths] = useState<Set<string>>(new Set());
  const [attempt, setAttempt] = useState(0);
  // Manual overrides beat every driver — clear them in the applied months so
  // revenue actually follows the forecast (on by default; undo restores them)
  const [clearOverrides, setClearOverrides] = useState(true);

  // Fetch the preview on mount (`attempt` lets the error state retry)
  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setError(null);

    aiRoomsForecastPreview({ versionId, departmentName, chartOfAccountDepartmentId: departmentId })
      .then(result => {
        if (cancelled) return;
        if (!result.available) {
          setUnavailableReason(result.reason);
          setPhase('unavailable');
          return;
        }
        setPreview(result);
        // Re-opening an active forecast keeps the months the background
        // refresh already maintains; otherwise every month we can forecast
        const applied = result.sourceStatus?.active ? result.sourceStatus.appliedMonths : [];
        setSelectedMonths(new Set(applied.length ? applied :
          Object.entries(result.monthly)
            .filter(([, m]) => (m.futureDays ?? m.daysCovered) > 0 && isForecastable(m))
            .map(([monthKey]) => monthKey)
        ));
        setPhase('preview');
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load AI rooms forecast preview:', err);
        setError(err instanceof Error ? err.message : 'Failed to load the rooms forecast preview');
        setPhase('error');
      });

    return () => { cancelled = true; };
  }, [versionId, departmentName, departmentId, attempt]);

  // Days grouped by 'YYYY-MM', in the monthly summary's sorted order
  const monthGroups = useMemo(() => {
    if (!preview) return [] as { monthKey: string; days: AiRoomsForecastDay[] }[];
    const byMonth = new Map<string, AiRoomsForecastDay[]>();
    for (const day of preview.days) {
      const key = day.date.slice(0, 7);
      if (!byMonth.has(key)) byMonth.set(key, []);
      byMonth.get(key)!.push(day);
    }
    const keys = new Set<string>([...Object.keys(preview.monthly), ...Array.from(byMonth.keys())]);
    return Array.from(keys)
      .sort()
      .map(monthKey => ({
        monthKey,
        days: (byMonth.get(monthKey) || []).slice().sort((a, b) => a.date.localeCompare(b.date)),
      }));
  }, [preview]);

  const forecastDays = useMemo(
    () => (preview ? preview.days.filter(d => !d.isActualized && selectedMonths.has(d.date.slice(0, 7))) : []),
    [preview, selectedMonths]
  );

  const toggleSelectedMonth = useCallback((monthKey: string) => {
    setSelectedMonths(prev => {
      const next = new Set(prev);
      if (next.has(monthKey)) next.delete(monthKey);
      else next.add(monthKey);
      return next;
    });
  }, []);

  const ownDriverAccounts = useMemo(
    () => (preview ? preview.targetAccounts.filter(a => a.hasAccountDriver) : []),
    [preview]
  );

  const toggleMonth = useCallback((monthKey: string) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(monthKey)) next.delete(monthKey);
      else next.add(monthKey);
      return next;
    });
  }, []);

  const overriddenMonths = useMemo(
    () => (preview?.overrides || []).filter(o => selectedMonths.has(o.monthKey)),
    [preview, selectedMonths]
  );

  const handleApply = useCallback(async () => {
    if (!preview || preview.targetAccounts.length === 0 || forecastDays.length === 0) return;

    setPhase('applying');
    setApplyError(null);
    try {
      const result = await aiRoomsForecastCommit({
        versionId,
        departmentName,
        chartOfAccountDepartmentId: departmentId,
        days: forecastDays.map(d => ({
          date: d.date,
          roomNights: d.forecastRooms,
          adr: d.adr,
        })),
        clearOverrideMonths: clearOverrides ? overriddenMonths.map(o => o.monthKey) : [],
      });
      onApplied(result);
      onClose();
    } catch (err) {
      console.error('Failed to apply AI rooms forecast:', err);
      setApplyError(err instanceof Error ? err.message : 'Failed to apply the rooms forecast');
      setPhase('preview');
    }
  }, [preview, forecastDays, versionId, departmentName, departmentId, clearOverrides, overriddenMonths, onApplied, onClose]);

  const isApplying = phase === 'applying';

  return (
    <div className="fv3-scrim fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
      <div className="fv3-dialog bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="fv3-dialog-head px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'var(--lodge-orange-50, var(--fv3-accent-wash, #fff7ed))' }}
            >
              <Sparkles size={18} style={{ color: 'var(--lodge-orange-500)' }} />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold leading-tight text-slate-900">
                AI Rooms Forecast
              </h2>
              {phase === 'preview' || phase === 'applying' ? (
                <p className="text-xs leading-tight" style={{ color: 'var(--lodge-onyx-500)' }}>
                  {preview?.curveInsufficient
                    ? 'On the books, anchored to last year / budget until enough pickup history builds up'
                    : <>On the books + historical pickup by days-out
                      {preview && typeof preview.curveSample === 'number'
                        ? ` (learned from ${preview.curveSample.toLocaleString()} stay dates)`
                        : ''}, anchored to last year / budget beyond that</>}
                </p>
              ) : (
                <p className="text-xs leading-tight" style={{ color: 'var(--lodge-onyx-500)' }}>
                  Daily room nights and ADR projected from OTB pace
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isApplying}
            className="p-1 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-40"
            title="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {phase === 'loading' && (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ background: 'var(--lodge-orange-50, var(--fv3-accent-wash, #fff7ed))' }}
              >
                <Loader2 size={24} className="animate-spin" style={{ color: 'var(--lodge-orange-500)' }} />
              </div>
              <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--lodge-onyx-800)' }}>
                Building the rooms forecast…
              </h3>
              <p className="text-xs" style={{ color: 'var(--lodge-onyx-500)' }}>
                Reading OTB snapshots and learning the pickup curve
              </p>
            </div>
          )}

          {phase === 'unavailable' && (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: 'var(--lodge-onyx-50)' }}>
                <Info size={24} style={{ color: 'var(--lodge-onyx-400)' }} />
              </div>
              <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--lodge-onyx-800)' }}>
                Rooms forecast not available
              </h3>
              <p className="text-xs mb-6 max-w-sm break-words" style={{ color: 'var(--lodge-onyx-500)' }}>
                {unavailableReason || 'There is not enough OTB snapshot history to learn a pickup curve yet.'}
              </p>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                style={{ color: 'var(--lodge-onyx-600)', border: '1px solid var(--lodge-onyx-200, var(--fv3-n-200, #cbd5e1))' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-onyx-50)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                Close
              </button>
            </div>
          )}

          {phase === 'error' && (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: '#fef2f2' }}>
                <AlertCircle size={24} style={{ color: '#dc2626' }} />
              </div>
              <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--lodge-onyx-800)' }}>
                Failed to load the rooms forecast
              </h3>
              <p className="text-xs mb-6 max-w-sm break-words" style={{ color: 'var(--lodge-onyx-500)' }}>
                {error || 'Something went wrong while building the preview.'}
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                  style={{ color: 'var(--lodge-onyx-600)', border: '1px solid var(--lodge-onyx-200, var(--fv3-n-200, #cbd5e1))' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--lodge-onyx-50)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  Close
                </button>
                <button
                  onClick={() => setAttempt(a => a + 1)}
                  className="px-4 py-2 text-sm font-semibold rounded-lg transition-all"
                  style={{ background: 'var(--lodge-orange-400)', color: 'var(--fv3-n-0, #ffffff)', border: 'none', boxShadow: 'var(--shadow-orange)' }}
                  onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)')}
                  onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)')}
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {(phase === 'preview' || phase === 'applying') && preview && (
            <div className="space-y-5">
              {/* Monthly summary with expandable daily detail */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-2">Forecast by month</h3>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs uppercase tracking-wide" style={{ background: 'var(--lodge-onyx-50)', color: 'var(--lodge-onyx-500)' }}>
                        <th className="w-8 px-3 py-2" aria-label="Apply" />
                        <th className="text-left font-medium px-3 py-2">Month</th>
                        <th className="text-right font-medium px-3 py-2">Days covered</th>
                        <th className="text-right font-medium px-3 py-2">Room nights</th>
                        <th className="text-right font-medium px-3 py-2">ADR</th>
                        <th className="text-right font-medium px-3 py-2">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthGroups.map(({ monthKey, days }) => {
                        const summary = preview.monthly[monthKey];
                        const isExpanded = expandedMonths.has(monthKey);
                        const hasFutureDays = days.some(d => !d.isActualized);
                        const lowSupport = hasFutureDays && !!summary && !isForecastable(summary);
                        const anchoredTo = hasFutureDays && summary?.referenceSource
                          && (summary.anchoredDays ?? 0) >= Math.max(1, (summary.futureDays ?? 0) / 2)
                          ? REFERENCE_LABELS[summary.referenceSource] : null;
                        return (
                          <React.Fragment key={monthKey}>
                            <tr
                              className="border-t border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors"
                              onClick={() => toggleMonth(monthKey)}
                            >
                              <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={selectedMonths.has(monthKey)}
                                  disabled={!hasFutureDays || isApplying}
                                  onChange={() => toggleSelectedMonth(monthKey)}
                                  title={hasFutureDays ? 'Apply this month' : 'Already actualized'}
                                  style={{ accentColor: 'var(--lodge-orange-500)' }}
                                />
                              </td>
                              <td className="px-3 py-2">
                                <span className="flex items-center gap-1.5 font-medium" style={{ color: 'var(--lodge-onyx-800)' }}>
                                  {isExpanded
                                    ? <ChevronDown size={14} className="flex-shrink-0" style={{ color: 'var(--lodge-onyx-400)' }} />
                                    : <ChevronRight size={14} className="flex-shrink-0" style={{ color: 'var(--lodge-onyx-400)' }} />}
                                  {formatMonthLabel(monthKey)}
                                  {lowSupport && (
                                    <span
                                      className="ml-1 px-1 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide"
                                      style={{ background: 'var(--lodge-onyx-100, var(--fv3-n-100, #e2e8f0))', color: 'var(--lodge-onyx-500)' }}
                                      title="No pickup history this far out and nothing to anchor to (no last-year actuals or budget) — this is mostly on-the-books"
                                    >
                                      little history
                                    </span>
                                  )}
                                  {!lowSupport && anchoredTo && (
                                    <span
                                      className="ml-1 px-1 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide"
                                      style={{ background: 'var(--lodge-orange-50, var(--fv3-accent-wash, #fff7ed))', color: 'var(--lodge-orange-600, var(--fv3-accent-ink, #ea580c))' }}
                                      title={`Too far out for learned pickup — on-the-books plus the expected final from ${anchoredTo}`}
                                    >
                                      anchored to {anchoredTo}
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--lodge-onyx-600)' }}>
                                {summary ? summary.daysCovered : days.length}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums font-medium" style={{ color: 'var(--lodge-onyx-800)' }}>
                                {formatRooms(summary ? summary.roomNights : days.reduce((s, d) => s + d.forecastRooms, 0))}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums" style={{ color: 'var(--lodge-onyx-600)' }}>
                                {formatCurrency(summary ? summary.adr : 0, true, currency)}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums font-medium" style={{ color: 'var(--lodge-onyx-800)' }}>
                                {formatCurrency(summary ? summary.revenue : days.reduce((s, d) => s + d.forecastRevenue, 0), true, currency)}
                              </td>
                            </tr>

                            {/* Daily detail */}
                            {isExpanded && (
                              <tr className="border-t border-slate-100">
                                <td colSpan={6} className="px-3 py-2" style={{ background: 'var(--lodge-onyx-50)' }}>
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr style={{ color: 'var(--lodge-onyx-500)' }}>
                                        <th className="text-left font-medium px-2 py-1">Date</th>
                                        <th className="text-right font-medium px-2 py-1">Days out</th>
                                        <th className="text-right font-medium px-2 py-1">OTB → forecast rooms</th>
                                        <th className="text-right font-medium px-2 py-1">ADR</th>
                                        <th className="text-right font-medium px-2 py-1">Revenue</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {days.map(day => (
                                        <tr
                                          key={day.date}
                                          className="border-t"
                                          style={{
                                            borderColor: 'var(--lodge-onyx-100, var(--fv3-n-100, #e2e8f0))',
                                            opacity: day.isActualized ? 0.55 : 1,
                                          }}
                                        >
                                          <td className="px-2 py-1 whitespace-nowrap" style={{ color: 'var(--lodge-onyx-700, var(--fv3-n-700, #334155))' }}>
                                            {formatDayLabel(day.date)}
                                            {day.isActualized && (
                                              <span
                                                className="ml-1.5 px-1 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide"
                                                style={{ background: 'var(--lodge-onyx-100, var(--fv3-n-100, #e2e8f0))', color: 'var(--lodge-onyx-500)' }}
                                              >
                                                actual
                                              </span>
                                            )}
                                          </td>
                                          <td className="px-2 py-1 text-right tabular-nums" style={{ color: 'var(--lodge-onyx-500)' }}>
                                            {day.isActualized ? '—' : day.daysOut}
                                          </td>
                                          <td className="px-2 py-1 text-right tabular-nums" style={{ color: 'var(--lodge-onyx-700, var(--fv3-n-700, #334155))' }}>
                                            {day.isActualized ? (
                                              formatRooms(day.forecastRooms)
                                            ) : (
                                              <>
                                                {formatRooms(day.otbRooms)}
                                                <span style={{ color: 'var(--lodge-onyx-400)' }}> → </span>
                                                <span className="font-medium" style={{ color: 'var(--lodge-onyx-800)' }}>
                                                  {formatRooms(day.forecastRooms)}
                                                </span>
                                                {day.pickupRooms > 0 && (
                                                  <span
                                                    className="ml-1"
                                                    style={{ color: 'var(--lodge-orange-600, var(--fv3-accent-ink, #ea580c))' }}
                                                    title={day.method === 'anchored' || day.method === 'blend'
                                                      ? `Expected final ${Math.round(day.referenceRooms ?? 0)} rooms from ${REFERENCE_LABELS[day.referenceSource ?? 'budget']}`
                                                      : 'Average historical pickup at this many days out'}
                                                  >
                                                    (+{day.pickupRooms.toFixed(1)})
                                                  </span>
                                                )}
                                                {(day.method === 'anchored' || day.method === 'blend') && day.referenceSource && (
                                                  <span className="ml-1 text-[10px] uppercase tracking-wide" style={{ color: 'var(--lodge-onyx-400)' }}>
                                                    {day.method === 'anchored' ? REFERENCE_LABELS[day.referenceSource] : 'blend'}
                                                  </span>
                                                )}
                                              </>
                                            )}
                                          </td>
                                          <td className="px-2 py-1 text-right tabular-nums" style={{ color: 'var(--lodge-onyx-600)' }}>
                                            {formatCurrency(day.adr, true, currency)}
                                          </td>
                                          <td className="px-2 py-1 text-right tabular-nums" style={{ color: 'var(--lodge-onyx-600)' }}>
                                            {formatCurrency(day.forecastRevenue, true, currency)}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Where the forecast lands — same distribution as every rooms driver */}
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-2">Applies to</h3>
                {preview.targetAccounts.length === 0 ? (
                  <p className="text-xs" style={{ color: '#b91c1c' }}>
                    No rooms revenue account was found to distribute the forecast across.
                  </p>
                ) : (
                  <div className="border border-slate-200 rounded-lg px-3 py-2.5 text-sm">
                    <div className="font-medium" style={{ color: 'var(--lodge-onyx-800)' }}>
                      {preview.targetDepartments.map(d => d.department).join(', ')}
                      <span className="font-normal" style={{ color: 'var(--lodge-onyx-500)' }}>
                        {' '}· Room Forecast driver · AI Rooms Forecast source
                      </span>
                    </div>
                    <div className="text-xs mt-1" style={{ color: 'var(--lodge-onyx-500)' }}>
                      Distributed across {preview.targetAccounts.length} revenue account
                      {preview.targetAccounts.length === 1 ? '' : 's'}:{' '}
                      {preview.targetAccounts.map(a => a.accountName).join(', ')}
                    </div>
                    {ownDriverAccounts.length > 0 && (
                      <div className="text-xs mt-1.5 flex items-start gap-1.5" style={{ color: '#b45309' }}>
                        <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
                        <span>
                          {ownDriverAccounts.map(a => a.accountName).join(', ')}{' '}
                          {ownDriverAccounts.length === 1 ? 'has its' : 'have their'} own account driver and
                          won&apos;t follow the forecast until {ownDriverAccounts.length === 1 ? 'it is' : 'they are'} reset
                          to the department driver.
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Manual overrides in the applied months */}
              {overriddenMonths.length > 0 && (
                <label
                  className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg text-xs leading-relaxed cursor-pointer"
                  style={{ background: 'var(--lodge-orange-50)', color: 'var(--lodge-onyx-700)' }}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 flex-shrink-0"
                    style={{ accentColor: 'var(--lodge-orange-400)' }}
                    checked={clearOverrides}
                    onChange={e => setClearOverrides(e.target.checked)}
                  />
                  <span>
                    <span className="font-medium" style={{ color: 'var(--lodge-onyx-900)' }}>
                      Clear manual overrides in {overriddenMonths.map(o => formatMonthLabel(o.monthKey)).join(', ')}
                    </span>
                    <br />
                    {overriddenMonths.reduce((n, o) => n + o.accounts, 0)} account-month
                    {overriddenMonths.reduce((n, o) => n + o.accounts, 0) === 1 ? '' : 's'} have a manual value
                    ({formatCurrency(overriddenMonths.reduce((n, o) => n + o.total, 0), true, currency)}).
                    Overrides beat every driver, so revenue in {overriddenMonths.length === 1 ? 'this month' : 'these months'} won&apos;t
                    follow the forecast while they&apos;re in place. Undo restores them.
                  </span>
                </label>
              )}

              {/* Coverage note */}
              <div
                className="flex items-start gap-2 px-3 py-2.5 rounded-lg text-xs leading-relaxed"
                style={{ background: 'var(--lodge-onyx-50)', color: 'var(--lodge-onyx-600)' }}
              >
                <Info size={14} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--lodge-onyx-400)' }} />
                <span>
                  Works like the On The Books and RMS sources: room revenue is split across the accounts
                  above by their revenue mix, room nights set the projected occupied rooms every
                  $/ORN line uses, and elapsed days always use actuals. Unchecked months keep the
                  system projection (or on-the-books). {forecastDays.length.toLocaleString()} future day
                  {forecastDays.length === 1 ? '' : 's'} will be applied. The checked months then refresh
                  automatically every day as new on-the-books data arrives
                  {formatRefreshed(preview.sourceStatus?.lastRefreshedAt)
                    ? ` (last refreshed ${formatRefreshed(preview.sourceStatus?.lastRefreshedAt)})`
                    : ''}.
                </span>
              </div>

              {/* Apply error */}
              {applyError && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-center gap-2">
                  <AlertCircle size={15} className="flex-shrink-0" />
                  {applyError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        {(phase === 'preview' || phase === 'applying') && preview && (
          <div className="fv3-dialog-foot px-6 py-4 border-t border-slate-200 flex gap-3 flex-shrink-0">
            <button
              onClick={onClose}
              disabled={isApplying}
              className="flex-1 py-2.5 border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={isApplying || preview.targetAccounts.length === 0 || forecastDays.length === 0}
              className="flex-1 py-2.5 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              style={{ background: 'var(--lodge-orange-400)', color: 'var(--fv3-n-0, #ffffff)', boxShadow: 'var(--shadow-orange)' }}
              onMouseEnter={e => { if (!(e.currentTarget as HTMLButtonElement).disabled) (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-500)'; }}
              onMouseLeave={e => { if (!(e.currentTarget as HTMLButtonElement).disabled) (e.currentTarget as HTMLButtonElement).style.background = 'var(--lodge-orange-400)'; }}
            >
              {isApplying ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Applying…
                </>
              ) : (
                <>
                  <Check size={16} />
                  Apply forecast
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AiRoomsForecastModal;
